<script setup lang="ts">
/**
 * What the user sees of the on-open `.zwdoc` conversion (#259): a blocking
 * notice while it runs, and the files that stopped it if it can't run
 * safely (nothing was changed in that case).
 */
import { computed } from "vue";
import { just } from "../lib/maybe";
import { isTauri } from "../lib/runtime";
import { navigateToSplash } from "../lib/tauri/core";
import { useProjectConversionStore } from "../stores/useProjectConversionStore";
import ZwButton from "./ui/ZwButton.vue";
import ZwDialog from "./ui/ZwDialog.vue";

const conversion = useProjectConversionStore();
const model = computed(() => conversion.model);

const converting = computed(() => model.value.phase === "converting");
const failed = computed(() =>
  model.value.phase === "failed" ? model.value : null,
);

function backToProjects(): void {
  conversion.dismiss();
  if (isTauri()) void navigateToSplash();
}
</script>

<template>
  <ZwDialog
    :open="converting"
    title="Updating this project"
    :description="
      just(
        'Converting documents to the new format. This happens once and is saved in the project history.',
      )
    "
    :close-on-backdrop="false"
    @close="() => {}"
  >
    <p class="zw-conversion-progress" role="status">
      Converting
      {{ model.phase === "converting" ? model.documents : 0 }} documents…
    </p>
  </ZwDialog>
  <ZwDialog
    :open="failed !== null"
    title="This project couldn't be updated"
    :description="
      just(
        'Nothing was changed. These documents can\'t be converted to the new format safely yet:',
      )
    "
    size="md"
    :close-on-backdrop="false"
    @close="backToProjects"
  >
    <ul v-if="failed !== null" class="zw-conversion-failures">
      <li v-for="failure in failed.failures" :key="failure.path">
        <strong>{{ failure.path }}</strong>
        <span>{{ failure.reason }}</span>
      </li>
      <li v-if="failed.failures.length === 0">{{ failed.error }}</li>
    </ul>
    <template #footer>
      <ZwButton variant="primary" @click="backToProjects">
        {{ isTauri() ? "Back to projects" : "Close" }}
      </ZwButton>
    </template>
  </ZwDialog>
</template>

<style scoped>
.zw-conversion-progress {
  margin: 0;
  color: var(--text-muted);
  font-family: var(--font-ui);
  font-size: 0.9rem;
}

.zw-conversion-failures {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 16rem;
  overflow-y: auto;
  font-family: var(--font-ui);
  font-size: 0.85rem;
}

.zw-conversion-failures li {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.zw-conversion-failures span {
  color: var(--text-muted);
}
</style>
